import * as React from 'react';
import {
 Tooltip, notification,
} from '@revfluence/fresh';
import { ChevronRightIcon } from '@revfluence/fresh-icons/regular';
import { useState } from 'react';
import { GetOfferById_offer } from '@frontend/applications/AffiliatesApp/queries/types/GetOfferById';
import { CreateCustomDeepLink, CreateCustomDeepLinkVariables } from '@frontend/applications/AffiliatesApp/queries/types/CreateCustomDeepLink';
import { CREATE_CUSTOM_DEEP_LINK_MUTATION_BULK } from '@frontend/applications/AffiliatesApp/queries/createCustomDeepLink';
import { useMutation } from '@apollo/client';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@frontend/shadcn/components/ui/sheet';
import { faArrowLeft, faTags } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@frontend/shadcn/components/ui/button';
import { faPencil, } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { validateDeepLink } from '../../../MembersWizard/utils/validateDeepLinkUrls';
import { TMemberTableRow } from '../../types';
import ManageDeepLinkViews from './Sections/ManageDeepLinkViews';
import { OfferDetailCard } from '../../../AffiliatesApp/components/OfferDetailCard';

interface IProps {
    className?: string;
    offer: GetOfferById_offer;
    isWorkFlow?: boolean;
    selectedMembers?: readonly TMemberTableRow[];
    selectedAffiliateIds: number[];
    onComplete?: () => void;
}

export interface DeepLinkType {
    affiliateOfferLinkId: number;
    deepLinkLabel: string;
    deepLinkUrl: string;
    isBrandCreated: boolean;
}

export const RefreshCreateAdditionalDeepLinks: React.FC<Readonly<IProps>> = React.memo((props) => {
    const {
        isWorkFlow, offer, selectedMembers, selectedAffiliateIds, onComplete,
    } = props;
    const [open, setOpen] = useState(false);
    const [isBulkCreate, setBulkCreate] = useState(true);
    const [bulkDeepLink, setBulkDeepLink] = useState<DeepLinkType[]>([]);
    const [individualDeepLinks, setIndividualDeepLinks] = useState<DeepLinkType[]>([]);
    const selectedActiveMembers = selectedMembers?.filter((member) => member.linkStatus.toLowerCase() === 'active') ?? [];

    const [createCustomDeepLink, { loading }] = useMutation<
        CreateCustomDeepLink,
        CreateCustomDeepLinkVariables
    >(CREATE_CUSTOM_DEEP_LINK_MUTATION_BULK, {
        onCompleted: () => {
            onClose(); // Close the drawer
            notification.success({
                message: 'Success',
                description: 'It may take a few minutes for the links to be created. Please refresh the page to see the updated links.',
            });
            setBulkDeepLink([]);
            setIndividualDeepLinks([]);
            onComplete();
        },
        onError: (error) => {
            notification.error({
                message: 'Error',
                description: `Failed to create DeepLink: ${error.message}`,
            });
        },
    });

    // Validation function for both bulk and individual views
    const areDeepLinksValid = () => {
        if (isBulkCreate) {
            const bulkLink = bulkDeepLink[0];
            const bulkLabel = bulkLink?.deepLinkLabel.trim();
            const bulkUrl = bulkLink?.deepLinkUrl.trim();
            const validation = validateDeepLink(bulkUrl || '');
            return bulkLabel && bulkUrl && !validation.error;
        } else {
            if (individualDeepLinks.length === 0) {
                return false;
            }
            return individualDeepLinks.every((link) => {
                const labelValid = link.deepLinkLabel.trim();
                const urlValid = link.deepLinkUrl.trim() && !validateDeepLink(link.deepLinkUrl).error;
                return labelValid && urlValid;
            });
        }
    };

    // Tooltip and error message logic
    const isError = selectedActiveMembers.length === 0
        ? 'You have not selected any active member. Please select an active member to proceed.'
        : !areDeepLinksValid()
            ? isBulkCreate
                ? 'Please enter a valid label and deep link URL for bulk create.'
                : 'Please fill in all individual labels and valid deep link URLs.'
            : '';

    const handleCreateLink = () => {
        if (!areDeepLinksValid()) {
            return;
        }

        createCustomDeepLink({
            variables: {
                data: isBulkCreate ? bulkDeepLink : individualDeepLinks,
            },
        });
    };

    const handleAdditionalDeepLinkCreate = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
      <>
        <Tooltip
          placement="top"
          title={!selectedAffiliateIds.length ? 'Select Members to Add Deep Links' : offer.archivedDate ? 'Cannot add links for Archived Offer' : 'Create Additional DeepLinks'}
        >
          <Button
            onClick={handleAdditionalDeepLinkCreate}
            disabled={!selectedAffiliateIds.length || offer.archivedDate}
            variant="ghost"
          >
            <FontAwesomeIcon icon={faPencil} className="text-grey-6 mr-2" /> Create Additional Links
          </Button>
        </Tooltip>

        <Sheet open={open} onOpenChange={onClose}>
          <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[576px] !max-w-[576px]">
            <SheetHeader className="p-4 flex items-center gap-2 flex-row">
              <FontAwesomeIcon icon={faTags} className="text-purple-5 w-6 h-6" />
              <SheetTitle className="!m-0">Create Additional Links</SheetTitle>
            </SheetHeader>
            <div className="flex items-center gap-2 px-4 py-2 bg-grey-1 border-t border-b border-grey-2">
              <Button
                onClick={onClose}
                key={2}
                className="w-8 h-8"
                variant="outline"
                size="sm"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-grey-6 w-4 h-4" />
              </Button>
              <OfferDetailCard
                showCta={false}
                isWorkFlow={isWorkFlow}
                offerData={offer}
                infoButtonClassName
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 160px)' }}>
              <ManageDeepLinkViews
                isBulkCreate={isBulkCreate}
                setBulkCreate={setBulkCreate}
                selectedMembers={selectedActiveMembers}
                setBulkDeepLink={setBulkDeepLink}
                individualDeepLinks={individualDeepLinks}
                setIndividualDeepLinks={setIndividualDeepLinks}
                offerData={offer}
              />
            </div>
            <SheetFooter className="justify-start sm:justify-start p-4 absolute bottom-0 left-0 right-0 w-full border-t bg-grey-0 z-[99]">
              <div className="flex gap-2 w-full">
                <div>
                  <Button onClick={onClose} variant="outline">
                    Cancel
                  </Button>
                </div>
                <div>
                  <Tooltip title={isError}>
                    <Button disabled={!!isError} loading={loading} onClick={handleCreateLink}>
                      Create Link
                      {' '}
                      <ChevronRightIcon />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
});

RefreshCreateAdditionalDeepLinks.defaultProps = {};
RefreshCreateAdditionalDeepLinks.displayName = 'RefreshCreateAdditionalDeepLinks';
